import React, {useEffect, useState, useRef, useContext, useMemo} from "react";
import { useSelector, useDispatch } from "react-redux";
import { GlobalStore } from "../../reduxStore";
import VehicleResult from "./VehicleResult";
import VehicleSearch from "./VehicleSearch";
import SearchSummaryBar from "./SearchSummaryBar";
import MobileStepper from "@mui/material/MobileStepper";
import { Button } from "@mui/material";
import SearchFilter from "./SearchFilter";
import { translateService, Translate } from "../../services/TranslateService";
import { Card } from "react-bootstrap";
import NoResults from "./NoResults";
import styles from "./VehicleSearch.module.scss";
import CustomTooltip from "../CustomTooltip";
import { IOnSale, IVehicleSearch } from "../../interfaces/VehicleSearch";
import moment from "moment";
import { extractCityFromPlace, extractPostalFromPlace } from "../../services/extractCityFromPlace";
import OnSale from "./OnSale/OnSale";
import { DataContext, ISearchParameters } from "../../contexts/dataContext";
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import DefaultVehicleResult from './DefaultVehicleResult';
import DefaultResults from "./DefaultResultsBeforeSearch.json";
import {isUnderTwentyOne} from "../MakeReservation/util";
import {Steps} from "../reservation-steps/steps";
import {OneWaySearchTab, SearchTab} from "./SearchTab/SearchTab";
import {ActiveFilterIndicators} from "./ActiveFilterIndicators/ActiveFilterIndicators";
import {scrollToElementWithOffset} from "../../GetUrlParams";
import { buildVehicleItem } from "../../services/ga4Ecommerce";
import {createSearchRequests} from "../../services/searchValidation";
import type {ISearchResponse} from "../../interfaces/VehicleSearch";
import {prepareDesktopMap} from "../Map/DeferredMap";
import { assertSearchResponse } from "../../services/searchResponse";

// Upper bound on the a2a result set fetched per search. The list shows 10 per page; everything
// beyond that exists only to drop pins on the map, and each vehicle costs ~6 KB on the wire and
// ~11 KB of sessionStorage. A large city was returning 430+ (2.5 MB, 3-6 s), so this trades a
// long tail of distant vehicles for a noticeably lighter desktop search. api3 caps mobile at 100
// on its own (mobile=true), so this bound only applies to desktop.
const MAX_SEARCH_RESULTS = 400;

interface IApiResult {
    count: number,
    vehicles: IVehicleSearch[],
    onsales: IOnSale[],
    extraOnsales?: IOnSale[]
}

export default function SearchResultsContainer({ setIsSearching, setRedirect }: {
  setIsSearching: Function,
  setRedirect: Function,
}) {
  const apiService = useSelector((state: GlobalStore) => state.chauffeurReducer.apiService);
  const dispatch = useDispatch();
  const [searchResults, setSearchResults] = useState<IVehicleSearch[]>([]);
  const [onSaleResults, setOnSaleResults] = useState<IOnSale[]>([]);
  const numPerPage = 10;
  const [page, _setPage] = React.useState(0);
  const [showBusy, setShowBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [totalVehicles, setTotalVehicles] = useState(0);
  const [showNothing, setShowNothing] = useState(false);
  const [detailsVisible, setDetailsVisible] = React.useState(-1);
  const dataContext = useContext(DataContext);
  const {route, mapData, showDefaultResults, changeVirtualPageInfo, oneWayRes, vehicle} = useContext(DataContext);
  const [showFilter, setShowFilter] = useState<boolean>(false); // controls filter view/re-renders only, results separated to show filter when no results on selected filters
  const [showResults, setShowResults] = useState<boolean>(false);
  const [currentPageResults, setCurrentPageResults] = useState<IVehicleSearch[]>( []);
  const [openFilters, setOpenFilters] = useState(false);
  // the full search form collapses into a sticky summary bar while results show (mobile + desktop)
  const [formCollapsed, setFormCollapsed] = useState<boolean>(false);
  const summaryBarActive = formCollapsed && !dataContext.oneWayRes;
  const latestFetch = useRef(0);
  const requestSearch = useMemo(() => createSearchRequests<ISearchResponse>(), [apiService]);
  const timeoutId = useRef(0);
  const defaultResults = DefaultResults.vehicles;

  useEffect(() => {
    if (route === '/' && !showDefaultResults && !vehicle) {
      if (!mapData?.length) {
        changeVirtualPageInfo(undefined);
      } else {
        changeVirtualPageInfo(!oneWayRes ? 'search' : 'onewaySearch')
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oneWayRes, route, showDefaultResults, mapData]);

  useEffect(() => {
    if (dataContext.onSalesAd !== undefined && dataContext.onSalesAd.length === 0
        && sessionStorage.getItem('retrievingOnSalesAd') !== '1' && apiService?.reservation) {
      sessionStorage.setItem('retrievingOnSalesAd', '1');

      apiService?.reservation.getOnSalesAd().then((response: any) => {
        if (response?.length === 0) {
          // no ads available
          dataContext.changeOnSalesAd(undefined);
        } else {
          dataContext.changeOnSalesAd(response);
        }
        sessionStorage.removeItem('retrievingOnSalesAd');
      }).catch(() => {
        // if error, set to undefined and not try fetch again
        sessionStorage.removeItem('retrievingOnSalesAd');
        dataContext.changeOnSalesAd(undefined);
      })
    }
  }, [dataContext.onSalesAd, apiService?.reservation]);

  useEffect(() => {
    if(dataContext.showDefaultResults && dataContext.mapEnabled && !dataContext.oneWayRes){
      dataContext.changeMapData(defaultResults ?? []); // set map data to default results
    }
  }, [dataContext.showDefaultResults, dataContext.mapEnabled, dataContext.oneWayRes])

  useEffect(() => {
    setDetailsVisible(-1)
  }, [page]);

  // GA4 view_item_list — fire once per distinct page of real search results
  // (results state settles over a few renders, so dedupe on a content signature).
  const lastViewedListSig = useRef<string>("");
  useEffect(() => {
    if (!showResults || !currentPageResults?.length) return;
    const listId = oneWayRes ? "oneway_list" : "a2a_list";
    const listName = oneWayRes ? "OneWay Results" : "A2A Results";
    const sig = `${listId}|${page}|${currentPageResults.map(v => v.id).join(",")}`;
    if (sig === lastViewedListSig.current) return;
    lastViewedListSig.current = sig;
    dataContext.sendGa4Event("view_item_list", {
      item_list_id: listId,
      item_list_name: listName,
      items: currentPageResults.map((v, i) =>
        buildVehicleItem(v, { noVat: dataContext.noVatPrices, index: page * numPerPage + i, listId, listName })
      ),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPageResults, showResults]);

  useEffect(() => {
    if(dataContext.enableOneWay && dataContext.oneWayRoutes.length === 0 && dataContext.oneWayRes){
      // one way must be selected to retrieved routes (to save app startup loading, etc.)
      // only get routes if entire app has oneway feature enabled
      // and when route array is empty (prevents recall every time user returns to search page)
      setShowBusy(true);
      apiService?.vehicle.getOneWayCityRoutes(dataContext.loggedIn ?? false)
          .then(r => {
              // No event on success — see the note above retrieveStarsData.
              dataContext.changeOneWayRoutes(r);
              setErrorMsg("")
              setShowBusy(false);
          })
          .catch(e => {
            setErrorMsg(translateService.translate('vehicleSearch.errorLoadingResults'));
            dataContext.sendGa4Event("resource_fetch_failed", { resource: "oneway_routes" });
            if (dataContext.appContext !== "production") console.log(e);
            setShowBusy(false);
          });
    }
  }, [apiService, dataContext.enableOneWay, dataContext.oneWayRoutes, dataContext.oneWayRes]);

  const checkCouponBeforeSearch = () => {
    // set coupon states if coupon was not set before search (login + user?.discount exists case)
    if (dataContext.searchParameters?.discount && dataContext.searchParameters?.discount !== '' &&
        dataContext.searchParameters?.discount !== dataContext.couponCode) {
      apiService?.vehicle.getCoupon(dataContext.searchParameters.discount)
          .then(r => {
            if (!r.valid) {
              // (this case is for user?.discount set from BE), so code should always be valid
              // if incorrect discount goes to BE then it just ignores it
              dataContext.changeStoredCoupon(null)
              sessionStorage.removeItem("coupon");
              sessionStorage.setItem('urlCodeValid', 'false');
            } else {
              sessionStorage.setItem('urlCodeValid', 'true');
              sessionStorage.setItem('coupon', JSON.stringify({ ...r, code: dataContext.searchParameters?.discount }));
              dataContext.changeStoredCoupon({...r, code: dataContext.searchParameters?.discount});
              dataContext.changeCouponDescription(r.description);
              dataContext.changeCouponCode(dataContext.searchParameters?.discount);
            }}).catch(e => {
        console.log('coupon fail ' + e);
        dataContext.changeStoredCoupon(null);
        sessionStorage.setItem('urlCodeValid', 'false');
        sessionStorage.removeItem("coupon");
      })
    }
  }

  useEffect(() => {
    //debounce
    clearTimeout(timeoutId.current)
    timeoutId.current = window.setTimeout(() => {
      doSearch().finally(() => {})
    }, 300);

    const UseSearchResults = (thisFetch: number, results: IApiResult) => {
      if (thisFetch === latestFetch.current) {
       let onSaleArr: IOnSale[] = [];
        if(results.onsales && results.onsales.length < 10 && results.extraOnsales && results.extraOnsales.length > 0){
          onSaleArr = [...results.onsales];
          let extraOnSaleArr: IOnSale[] = [...results.extraOnsales];

          onSaleArr = onSaleArr.concat(extraOnSaleArr);

        } else {
          onSaleArr = [...results.onsales];
        }
        setSearchResults(results.vehicles ?? []);
        setOnSaleResults(onSaleArr ?? []);
        setTotalVehicles(results.count);
        setErrorMsg("");
        if (dataContext.user)
          sessionStorage.setItem("user", JSON.stringify(dataContext.user));
        sessionStorage.setItem("time", moment().format())
        let scrollAnchorEle = document.getElementById("scrollAnchor");
        if (scrollAnchorEle) {
          scrollToElementWithOffset(scrollAnchorEle, 50); // 50px: to account for some of the header space
        }
      }
    }

    const doSearch = async () => {
      setSearchResults([]);
      if(!dataContext.oneWayRes){
        if(!dataContext.searchParameters?.location || !apiService) {
          setShowBusy(false);
          return;
        }
      } else {
        if(!dataContext.searchParameters?.pickupCity || !dataContext.searchParameters?.returnCity || !apiService) {
          setShowBusy(false);
          return;
        }
      }
      if (dataContext.route === '/') {changeVirtualPageInfo(undefined)}

      dataContext.changeShowDefaultResults(false);
      dataContext.changeMapData([]);
      setShowBusy(true);
      const thisFetch = ++latestFetch.current;
      if (dataContext.mapEnabled && !dataContext.mobileDevice) prepareDesktopMap();

      // Location + dates + first (original) intent for GA4 funnel analysis. City is always
      // available; postal code only for street/zip searches. first_segment is set once per
      // session (see dataContext) and reflects the original car/van intent, unchanged by later toggles.
      const sp = dataContext.searchParameters;
      const fmtDate = (d: moment.MomentInput) => d ? moment(d).format('YYYY-MM-DD') : undefined;
      const dateMeta = { start_date: fmtDate(sp?.startDate), end_date: fmtDate(sp?.endDate),
                         first_segment: sessionStorage.getItem('first_segment') ?? undefined };
      const a2aMeta = { ...dateMeta, pickup_city: sp?.searchCity ?? extractCityFromPlace(sp?.location), pickup_postal_code: extractPostalFromPlace(sp?.location) };
      const onewayMeta = { ...dateMeta, pickup_city: sp?.pickupCity, dropoff_city: sp?.returnCity };

      if(!dataContext.oneWayRes) {
        checkCouponBeforeSearch(); // if user logs in without opening filters, coupon may not set correctly,
        // this will correctly set all coupon values for services to be used calculated correctly

        try {
          const params = { ...dataContext.searchParameters, showStart: 0, showAmount: MAX_SEARCH_RESULTS };
          // List
          // we take the whole result set in one call because api3 is so slow, show 10 of them in the
          // list view and keep the rest for the map
          const mapData = await requestSearch(JSON.stringify(["a2a", !!dataContext.loggedIn, params]), async () => assertSearchResponse(await apiService.vehicle.searchWithParams(params, !!dataContext.loggedIn)));
          if (thisFetch !== latestFetch.current) return;
          const start = dataContext.searchParameters.showStart === undefined ? 0 : dataContext.searchParameters.showStart;
          const end = start + (dataContext.searchParameters.showAmount === undefined ? 10 : dataContext.searchParameters.showAmount);
          let results = {
                        count: mapData.count ?? 0,
                        vehicles: mapData.vehicles.slice(start,end),
                        // onsales is genuinely optional — api3 omits both keys when onsales is off
                        onsales: mapData.onsales ?? [],
                        extraOnsales: mapData.extraOnsales ?? []
          };
          dataContext.sendGa4Event("search", {
              search_term: "query",
              search_type: "a2a",
              search_result: "success",
              results_count: results.count,
              ...a2aMeta
          });
          UseSearchResults(thisFetch, results);
          // Map
          if(dataContext.mapEnabled){
            // We do not have to get another set of results as we already have all of them
            dataContext.changeMapData(mapData.vehicles ?? []);
          }
          setShowBusy(false);
        } catch (e) {
          if (thisFetch !== latestFetch.current) return;
          setErrorMsg(translateService.translate('vehicleSearch.errorLoadingResults'));
          if (dataContext.appContext !== "production") console.log(e);
          dataContext.sendGa4Event("search", {
              search_term: "query",
              search_type: "a2a",
              search_result: "failure",
              results_count: 0,
              ...a2aMeta
          });
          setShowBusy(false);
          let scrollAnchorEle = document.getElementById("scrollAnchor");
          if (scrollAnchorEle) {scrollToElementWithOffset(scrollAnchorEle, 50)}
        }
      } else {
        try {
          const params = { ...dataContext.searchParameters, showStart: 0, showAmount: 1000 };
          // List
          const mapData = await requestSearch(JSON.stringify(["oneway", !!dataContext.loggedIn, params]), async () => assertSearchResponse(await apiService.vehicle.oneWaySearchWithParams(params, dataContext.loggedIn ?? false)));
          if (thisFetch !== latestFetch.current) return;
          let results: IApiResult = {
            count: mapData.count ?? 0,
            vehicles: mapData.vehicles, //.slice(start,end),
            // do not restrict one-way count, this will be used for sorting and will get updated after sorting and...
            // changing page or searchResults values will select the required data for displaying per page view (under 10)
            onsales: mapData.onsales ?? [],
            extraOnsales: mapData.extraOnsales ?? []
          };
          dataContext.sendGa4Event("search", {
              search_term: "query",
              search_type: "oneway",
              search_result: "success",
              results_count: results.count,
              ...onewayMeta
          });
          UseSearchResults(thisFetch, results);
          // Map
          if(dataContext.mapEnabled){
            dataContext.changeMapData(mapData.vehicles ?? []);
          }
          setShowBusy(false);
        } catch (e) {
          if (thisFetch !== latestFetch.current) return;
          setErrorMsg(translateService.translate('vehicleSearch.errorLoadingResults'));
          if (dataContext.appContext !== "production") console.log(e);
          dataContext.sendGa4Event("search", {
              search_term: "query",
              search_type: "oneway",
              search_result: "failure",
              results_count: 0,
              ...onewayMeta
          });
          setShowBusy(false);
          let scrollAnchorEle = document.getElementById("scrollAnchor");
          if (scrollAnchorEle) {scrollToElementWithOffset(scrollAnchorEle, 50)}
        }
      }
      setShowNothing(false);
      setIsSearching(true)
    }
    return () => {clearTimeout(timeoutId.current); latestFetch.current++;};
  }, [dataContext.searchParameters]);

  useEffect(() => {
    const reservationCreated = sessionStorage.getItem("reservationCreated") ?? undefined;
    const paymentPending = sessionStorage.getItem('paymentPending') ?? undefined;
    if (reservationCreated && !paymentPending) {
      // Reservation flag is hydrated but no payment is in flight — the user has
      // navigated back to search after a stale session, so wipe selections so
      // they don't re-enter a half-finished flow.
      if (reservationCreated === 'true') {
        dataContext.clearSelections();
        dispatch({ type: "CLEAR_SELECTIONS", payload: null });
      }
    }

    // #todo: do we need this coupon retrieval here still?
    let c = sessionStorage.getItem("coupon");
    if (c) {
      dataContext.changeStoredCoupon(JSON.parse(c!));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function setSearchParams(v: any) {
    let params: ISearchParameters;
    if (typeof v === 'function')
      params = v(dataContext.searchParameters);
    else
      params = v;
    if (params) {
      dataContext.changeSearchParameters(params);
    }
  }

  function selectVehicle(vehicle: IVehicleSearch) {
    if (vehicle.id !== dataContext.vehicle?.id) {
      sessionStorage.removeItem("customer");
      sessionStorage.removeItem("services");
    }

    if(dataContext.customer && dataContext.vehicle){
      if( dataContext.customer.dateOfBirth !== '' ||  dataContext.customer.personalIdCode !== ''){
        if(isUnderTwentyOne(dataContext.customer.dateOfBirth,  dataContext.customer.personalIdCode) &&  vehicle){
          if(vehicle.vehicleData.noundertwentyone){
            dataContext.changeTwentyModal(true);
            return;
          }
        }
      }
    }

    dataContext.changeVehicle(vehicle);
    dataContext.changeMapData([vehicle])
    dataContext.changeSearchParameters(dataContext.searchParameters);

    if (dataContext.user)
      sessionStorage.setItem("user", JSON.stringify(dataContext.user));
      sessionStorage.setItem("page", "1")
      dataContext.changeRoute("/makereservation")
      dataContext.changePage(1)
  }

  const clearResults = () => {
    setSearchResults([]);
    dataContext.changeMapData([]);
    setTotalVehicles(0);
    setShowNothing(true);
    setShowFilter(false);
    setShowResults(false);
  };

  const setPage = (p: number) => {
    _setPage(() => {
      const newPage = p;
      if (!dataContext.oneWayRes) {
        if(dataContext.filteredVehicles){
          const startIndex = newPage * numPerPage;
          const endIndex = Math.min(startIndex + numPerPage, dataContext.filteredVehicles?.length);
          setCurrentPageResults(dataContext.filteredVehicles.slice(startIndex, endIndex));
        } else {
          setCurrentPageResults([]);
        }
      } else {
        if(searchResults) {
          const startIndex = page * numPerPage;
          const endIndex = Math.min(startIndex + numPerPage, totalVehicles);
          setCurrentPageResults(searchResults.slice(startIndex, endIndex));
        } else {
          setCurrentPageResults([]);
        }
      }
      return newPage;
    });
  };

  useEffect(() => {
    if (!dataContext.oneWayRes) {
      if(dataContext.filteredVehicles){
        const startIndex = page * numPerPage;
        const endIndex = Math.min(startIndex + numPerPage, dataContext.filteredVehicles?.length);
        setCurrentPageResults(dataContext.filteredVehicles.slice(startIndex, endIndex));
      } else {
        setCurrentPageResults([]);
      }
    } else {
      if(searchResults.length) {
        const startIndex = page * numPerPage;
        const endIndex = Math.min(startIndex + numPerPage, totalVehicles);
        setCurrentPageResults(searchResults.slice(startIndex, endIndex));
      } else {
        setCurrentPageResults([]);
      }
    }
  }, [dataContext.filteredVehicles, page, numPerPage, searchResults]);

  // Fetching testimonials.json (the review-star ratings) is a static asset load, not a
  // user search — but it used to fire GA4 `search`, on every results render, whether or
  // not anyone searched. Together with the one-way route lookup above that was 110,439 of
  // 245,010 `search` events in a 30-day window: 45% of the funnel's entry step was noise,
  // which made the search->select drop-off unreadable. `search` now means a search.
  //
  // Only FAILURES are reported now, as `resource_fetch_failed`. The successful case was
  // ~110k events a month recording that a file loaded, which nobody reads; the failures
  // are the part worth alerting on. Note the event name says "failed" deliberately — an
  // event called `resource_fetch` that only fired on failure would invite someone to
  // compute a failure rate from it and get 100%.
  //
  // Trade-off: without the success event there is no exact denominator, so absolute
  // failure counts replace a failure rate. Both fetches run at most once per session
  // (sessionStorage-cached), so failures/sessions is a fair approximation if a rate is
  // ever needed.
  //
  // `search_term: "query"` was dropped too: that literal was filling GA4's built-in
  // Search term dimension with the word "query".
  const retrieveStarsData = async () => {
    sessionStorage.setItem('starsUpdatedDS', '1'); // 1 indicates that retrieval in progress
    try{
      const response = await fetch(dataContext.rootUrl+'/testimonials.json');
      const data = await response.json();
      // 'starsUpdatedDS': stars data update date time value (only fetch if last update was > 24 hours ago)
      // using sessionStorage as it updates faster than react states (used to prevent fetch before last call gets return value)
      sessionStorage.setItem('starsUpdatedDS', moment().format('DD.MM.YYYY HH:mm').toString())
      dataContext.changeStars(data);
    } catch (e) {
      if (dataContext.appContext !== "production") console.log(e);
      // remove item indicator so fetch can be tried again on next search:
      sessionStorage.removeItem('starsUpdatedDS');
      dataContext.sendGa4Event("resource_fetch_failed", { resource: "testimonials" });
    }
  }

  const getStarValue = () => {
    const starsLastRetrievedData = sessionStorage.getItem('starsUpdatedDS') ?? undefined;
    if (starsLastRetrievedData === '1') return; // retrieval in progress
    if(!starsLastRetrievedData || starsLastRetrievedData === '0') {
      retrieveStarsData();
    } else if (starsLastRetrievedData && starsLastRetrievedData !== '0') {
      if (moment(starsLastRetrievedData, 'DD.MM.YYYY HH:mm').isValid() &&
          moment(starsLastRetrievedData, 'DD.MM.YYYY HH:mm') < moment().subtract(24, 'hours'))
      {
        retrieveStarsData();
      }
    }
  }

  const retrieveAcrissData = async () => {
    sessionStorage.setItem('acrissCodesUpdatedDS', '1'); // 1 indicates that retrieval in progress
    try{
      const response = await fetch(dataContext.v3ApiUrl + '/anonymous/vehicles/get-acriss');
      const data = await response.json();
      // 'acrissCodesUpdatedDS': stars data update date time value (only fetch if last update was > 24 hours ago)
      // using sessionStorage as it updates faster than react states (used to prevent fetch before last call gets return value)
      sessionStorage.setItem('acrissCodesUpdatedDS', moment().format('DD.MM.YYYY HH:mm').toString())
      dataContext.changeAcrissCodeSet(data.data);
    } catch (e) {
      if (dataContext.appContext !== "production") console.log(e);
      // remove item indicator so fetch can be tried again on next search:
      sessionStorage.removeItem('acrissCodesUpdatedDS');
    }
  }

  const getAcrissValues = () => {
    const acrissLastRetrievedData = sessionStorage.getItem('acrissCodesUpdatedDS') ?? undefined;
    if (acrissLastRetrievedData === '1') return; // retrieval in progress
    if(!acrissLastRetrievedData || acrissLastRetrievedData === '0') {
      retrieveAcrissData();
    } else if (acrissLastRetrievedData && acrissLastRetrievedData !== '0') {
      if (moment(acrissLastRetrievedData, 'DD.MM.YYYY HH:mm').isValid() &&
          moment(acrissLastRetrievedData, 'DD.MM.YYYY HH:mm') < moment().subtract(24, 'hours'))
      {
        retrieveAcrissData();
      }
    }
  }

  useEffect(() => {
    if(searchResults.length !==0 && !showBusy ){
      setShowFilter(true);
      setShowResults(true);
    } else if (searchResults.length===0 && openFilters && !showBusy && !showNothing) {
      // no results with filtered selections
      setShowFilter(true);
      setShowResults(false);
    } else {
      setShowFilter(false);
      setShowResults(false);
    }
    getStarValue();
    getAcrissValues();
  }, [showBusy, searchResults]);

  // Collapse the search form into the sticky summary bar whenever a search runs or results are
  // visible (mobile + desktop). Expanding back is manual (the bar's Muokkaa / edit button).
  useEffect(() => {
    if (!dataContext.oneWayRes && (showBusy || showResults)) {
      setFormCollapsed(true);
    }
  }, [showBusy, showResults, dataContext.oneWayRes]);

  const expandSearchForm = () => {
    setFormCollapsed(false);
    // let the form render, then bring the location field into view
    setTimeout(() => document.getElementById('google-search-location')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
  };

  // The map view's summary bar (rendered in App) asks for the form via this event — the map
  // closes and the search form expands in the list view.
  useEffect(() => {
    const handler = () => expandSearchForm();
    window.addEventListener('rrm-expand-search', handler);
    return () => window.removeEventListener('rrm-expand-search', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hide App's floating map button while the sticky summary bar is visible so it never overlaps —
  // the sticky bar owns map open/close instead (rule in index.scss).
  // When the full search form is expanded (summaryBarActive false), the floating button reappears.
  const hideFloatingMapBtn = !!dataContext.mobileDevice && summaryBarActive;
  useEffect(() => {
    if (hideFloatingMapBtn) { document.body.setAttribute('data-rrm-summary-bar', '1'); }
    else { document.body.removeAttribute('data-rrm-summary-bar'); }
    return () => { document.body.removeAttribute('data-rrm-summary-bar'); };
  }, [hideFloatingMapBtn]);

  useEffect(() => {
    if(page !== 0) { // only do this once
      setPage(0);
    }
  }, [dataContext.filteredParams, searchResults, dataContext.sortingTab]);

  const PageStepper = () => {
    const totalPages = !dataContext.oneWayRes ? Math.ceil((dataContext.filteredVehicles?.length || 0) / numPerPage) :
        Math.ceil((totalVehicles || 0) / numPerPage);

    return (
        <MobileStepper
            steps={totalPages}
            variant="text"
            style={{
              flexGrow: 1,
              borderRadius: '8px',
              boxShadow: '0 3px 6px rgb(0 0 0 / 12%)',
              marginBottom: '1.5rem'
            }}
            activeStep={page}
            position="static"
            backButton={
              <span>
          <CustomTooltip className="tooltip-link"
                 zIndex={2}
                 content={<Translate content={'vehicleSearch.firstPage'} />}
          >
            <Button size="small"
                    className={styles.prevNextButton}
                    variant="text"
                    onClick={() => {
                      setPage(0);
                      let scrollAnchorEle = document.getElementById("scrollAnchor");
                      if (scrollAnchorEle) {scrollToElementWithOffset(scrollAnchorEle, 50)}
                      dataContext.sendGa4Event("paginate_search_results", {
                        direction: "home",
                        page_number: 1,
                        search_type: "a2a"
                      });
                    }}
                    disabled={page <= 0}>
              &lt;&lt;
            </Button>
          </CustomTooltip>
          <Button size="small"
                  className={styles.prevNextButton}
                  onClick={() => {
                    setPage(page - 1);
                    let scrollAnchorEle = document.getElementById("scrollAnchor");
                    if (scrollAnchorEle) {scrollToElementWithOffset(scrollAnchorEle, 50)}
                    dataContext.sendGa4Event("paginate_search_results", {
                      direction: "previous",
                      page_number: page-1,
                      search_type: "a2a"
                    });
                  }}
                  disabled={page <= 0}>
            &lt;
          </Button>
        </span>
            }
            nextButton={
              <span>
          <Button size="small"
                  className={styles.prevNextButton}
                  onClick={() => {
                    setPage(page + 1);
                    let scrollAnchorEle = document.getElementById("scrollAnchor");
                    if (scrollAnchorEle) {scrollToElementWithOffset(scrollAnchorEle, 50)}
                    dataContext.sendGa4Event("paginate_search_results", {
                      direction: "next",
                      page_number: page+1,
                      search_type: "a2a"
                    });
                  }}
                  disabled={page + 1 >= totalPages}>
            &gt;
          </Button>
          <CustomTooltip className="tooltip-link"
                 zIndex={2}
                 content={<Translate content={'vehicleSearch.lastPage'} />}
          >
            <Button size="small"
                    className={styles.prevNextButton}
                    onClick={() => {
                      setPage(totalPages - 1);
                      let scrollAnchorEle = document.getElementById("scrollAnchor");
                      if (scrollAnchorEle) {scrollToElementWithOffset(scrollAnchorEle, 50)}
                      dataContext.sendGa4Event("paginate_search_results", {
                        direction: "end",
                        page_number: totalPages - 1,
                        search_type: "a2a"
                      });
                    }}
                    disabled={page + 1 >= totalPages}>
              &gt;&gt;
            </Button>
          </CustomTooltip>
        </span>
            }
        />
    )
  }

  return <div className={styles.searchContainer}
              style={{maxWidth: (oneWayRes && !(sessionStorage.getItem('oneWaySearchInit') ?? undefined)) ? 'max-content' : '768px'}}>
    {summaryBarActive &&
        <SearchSummaryBar
            location={dataContext.searchParameters?.location}
            pickupDate={dataContext.searchParameters?.pickupDate}
            returnDate={dataContext.searchParameters?.returnDate}
            onEdit={expandSearchForm}
            setOpenFilters={setOpenFilters}
            loading={showBusy}
            // Opens the fullscreen map (App owns the state; the floating .mapBtn is hidden while
            // the bar is active so it never overlaps the map). Mobile only — desktop shows the
            // side map already and keeps its own map controls.
            onMapToggle={() => window.dispatchEvent(new Event('rrm-open-map'))}
            showMapButton={!!(dataContext.mobileDevice && dataContext.mapEnabled && dataContext.searchParameters?.location && !dataContext.vehicle)}
        />}
    <div style={summaryBarActive ? { display: 'none' } : undefined}>
        <VehicleSearch isSearching={showBusy} setErrorMsg={setErrorMsg} setSearchParams={setSearchParams} clearResults={clearResults} />
    </div>
    {!!errorMsg &&
        <Card className={`card-body mb-4`}>
          <div className="text-center pt-3">{errorMsg}</div>
        </Card>}
    <span id='scrollAnchor'></span>
    <Backdrop
        sx={{ color: '#fbb040', zIndex: (theme) => theme.zIndex.drawer + 10005 }}
        open={showBusy} >
      <CircularProgress color="inherit" />
      <div style={{marginLeft: '5px'}}><Translate content={'vehicleSearch1.loading'} /></div>
    </Backdrop>
    { !dataContext.oneWayRes && dataContext.showDefaultResults ? <div>
      <div style={{fontWeight: 600, textAlign:"center", justifyContent: "center", padding: 10, fontSize: 18, fontStyle: "italic" }}>
        {translateService.translate('vehicleSearch1.belowSamplesFillLocation')}
      </div>
      { defaultResults && defaultResults.map((vehicle, i) => {
        return <DefaultVehicleResult key={i} vehicle={vehicle}
                                     noVat={dataContext.noVatPrices}
                                     setRedirect={setRedirect}
                                     selectVehicle={()=> {
                                       document.getElementById("scrollAnchor")?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                       (document.querySelector('.searchbox-input') as HTMLElement)?.focus();
                                     }}
                                     detailsVisible={i === detailsVisible}
                                     setDetailsVisible={() => {setDetailsVisible(() => { return i === detailsVisible ? -1 : i });
                                        dataContext.sendGa4Event("view_item", { items: [buildVehicleItem(vehicle, { noVat: dataContext.noVatPrices })] });}}
        /> })
      }
      <PageStepper/>
    </div> :
        <>
          {!dataContext.oneWayRes ?
              showResults && dataContext.filteredVehicles && dataContext.filteredVehicles.length > 0 && <Steps /> :
              showResults && searchResults.length > 0 && <Steps/> }
          {showFilter && !dataContext.oneWayRes ? <SearchFilter openFilters={openFilters} setCloseFilters={setOpenFilters} /> : null }
        {showResults && !dataContext.oneWayRes && dataContext.filteredVehicles && dataContext.filteredVehicles.length > 0 ?
            <OnSale sales={onSaleResults} selectVehicle={selectVehicle} onSalesAd={dataContext.onSalesAd}/> : null}
        {/* On mobile the sticky summary bar already carries the location, dates, segment and
            filter opener, so the active-filter indicators row is redundant there — show it only
            when the bar isn't active (desktop, or while the form is expanded). */}
        {!summaryBarActive && !dataContext.oneWayRes && showResults && dataContext.filteredVehicles && dataContext.filteredVehicles.length > 0 ?
            <ActiveFilterIndicators setOpenFilters={setOpenFilters} /> : null}
        {!dataContext.oneWayRes && showResults && dataContext.filteredVehicles && dataContext.filteredVehicles.length > 0 ?
            <SearchTab /> : null}
          {dataContext.oneWayRes && showResults && searchResults ?
              <OneWaySearchTab oneWayVehicleList={dataContext.mapData} setSearchResults={setSearchResults} noVatPrices={dataContext.noVatPrices}/> : null }
        {!dataContext.oneWayRes ?
            dataContext.filteredVehicles && dataContext.filteredVehicles.length > 0 && showResults ? (
                <>
                  {!showBusy && currentPageResults.map((vehicle, i) => {
                    return(
                      <VehicleResult key={i} vehicle={vehicle}
                                     stars={dataContext.stars ? dataContext.stars[vehicle.id] : undefined}
                                     setRedirect={setRedirect} selectVehicle={selectVehicle}
                                     detailsVisible={i === detailsVisible}
                                     setDetailsVisible={() => {setDetailsVisible(() => { return i === detailsVisible ? -1 : i });
                                       dataContext.sendGa4Event("view_item", { items: [buildVehicleItem(vehicle, { noVat: dataContext.noVatPrices })] });}}
                                     setBusy={setShowBusy}
                                     noVat={dataContext.noVatPrices}/>
                    )
                  })
                  }
                  <PageStepper/>
                </>
            ) : <> {(!showBusy && !showNothing && dataContext.filteredVehicles?.length === 0) ? <NoResults /> : null} </>
            :
            <div> {searchResults && showResults ? (
                    <>
                      {!showBusy && currentPageResults.map((vehicle, i) => {
                        return <VehicleResult key={i} vehicle={vehicle}
                                              stars={dataContext.stars ? dataContext.stars[vehicle.id] : undefined}
                                              setRedirect={setRedirect} selectVehicle={selectVehicle}
                                              detailsVisible={i === detailsVisible}
                                              setDetailsVisible={() => {setDetailsVisible(() => { return i === detailsVisible ? -1 : i });
                                                dataContext.sendGa4Event("view_item", { items: [buildVehicleItem(vehicle, { noVat: dataContext.noVatPrices })] });}}
                                              setBusy={setShowBusy}
                                              noVat={dataContext.noVatPrices}/>
                      })
                      }
                      <PageStepper/>
                    </>
                )
                : <> {(!showBusy && !showNothing) && <NoResults /> } </> }
            </div>
        }
      </>
    }
  </div>
}
